/**
 * Turns a student's `duplicateLogs` into something readable.
 *
 * The backend detects duplicates three ways (StudentController.addDuplicateLogs):
 *   referenceId  -> same_reference_id
 *   universityId -> same_university_id
 *   detailed     -> same_name_dob_university_degree_program
 * plus two reasons that can be set outside that flow.
 */

export const DUPLICATE_REASONS = {
  same_reference_id: {
    label: "Same Application / Reference ID",
    detail: "Both applications were submitted with the same reference ID.",
  },
  same_university_id: {
    label: "Same Student / University ID",
    detail: "Both applications carry the same university-issued student ID.",
  },
  same_name_dob_university_degree_program: {
    label: "Same name, date of birth, university, degree and program",
    detail: "Every one of those details matches an existing application.",
  },
  same_email_passport: {
    label: "Same email and passport number",
    detail: "Both applications share an email address and passport number.",
  },
  manual_duplicate_marking: {
    label: "Marked manually",
    detail: "Someone flagged this application as a duplicate by hand.",
  },
};

const FIELD_LABELS = {
  studentFirstName: "First name",
  studentLastName: "Last name",
  dateOfBirth: "Date of birth",
  university: "University",
  degreeType: "Degree",
  program: "Program",
  referenceId: "Reference ID",
  universityId: "University ID",
  studentEmailId: "Email",
  passportNumber: "Passport number",
};

const nameOf = (log) => {
  const populated = log?.duplicateOfStudentId;
  const fromPopulated =
    populated && typeof populated === "object"
      ? `${populated.studentFirstName || ""} ${populated.studentLastName || ""}`.trim()
      : "";
  return (log?.duplicateOfStudentName || fromPopulated || "").trim();
};

const caeIdOf = (log) => {
  const populated = log?.duplicateOfStudentId;
  const fromPopulated =
    populated && typeof populated === "object" ? populated.uniqueCaeId : "";
  return log?.duplicateOfUniqueCAEId || fromPopulated || "";
};

/**
 * One readable entry per unresolved duplicate log.
 *
 * Resolved logs are kept in the database as history, so they are filtered out -
 * the badge is about why this row is flagged *now*.
 */
export const describeDuplicate = (row) => {
  const logs = Array.isArray(row?.duplicateLogs) ? row.duplicateLogs : [];

  const seen = new Set();
  const entries = [];

  logs
    .filter((log) => log && !log.isResolved)
    .forEach((log) => {
      const reason = DUPLICATE_REASONS[log.duplicateReason] || {
        label: "Flagged as a duplicate",
        detail: "",
      };
      const name = nameOf(log);
      const caeId = caeIdOf(log);

      // The same pair can be logged more than once over time.
      const key = `${caeId || name}::${log.duplicateReason}`;
      if (seen.has(key)) return;
      seen.add(key);

      entries.push({
        name: name || "another application",
        caeId,
        reason: log.duplicateReason,
        reasonLabel: reason.label,
        reasonDetail: log.duplicateReasonDetails || reason.detail,
        fields: (log.matchingFields || [])
          .map((field) => FIELD_LABELS[field] || field)
          .filter(Boolean),
        detectedOn: log.detectedOn || null,
      });
    });

  return entries;
};

/** Plain-text version, for a title attribute or a test. */
export const duplicateSummaryText = (row) => {
  const entries = describeDuplicate(row);
  if (!entries.length) return "";

  return entries
    .map((entry) => {
      const who = entry.caeId ? `${entry.name} (${entry.caeId})` : entry.name;
      const fields = entry.fields.length
        ? ` — matched on ${entry.fields.join(", ")}`
        : "";
      return `${who}: ${entry.reasonLabel}${fields}`;
    })
    .join("\n");
};
